import { __decorate, __metadata } from "tslib";
import { PLATFORM_ID } from '@angular/core';
import { AccessService, PersistStateFactory } from '@core/frontend-shared';
import { AppRequestService } from '@core/frontend-shared/api';
import { ConfigService } from '@core/shared/config';
import { CheckMaintenanceInfoRequest } from '@common/maintenance/shared';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { dayjs } from '@core/shared/utils/dayjs';
import { isPlatformServer } from '@angular/common';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "@core/shared/config";
import * as i3 from "@core/frontend-shared";
import * as i4 from "@core/frontend-shared/api";
// Interesting other concept: https://blog.nodeswat.com/automagic-reload-for-clients-after-deploy-with-angular-4-8440c9fdd96c
const STORAGE_ID_DISMISSED_DATE = 'maintenance-dismissed';
export const MaintenanceActions = {
  FetchAnnouncement: class {
    static {
      this.type = '[Maintenance] FetchAnnouncement';
    }
    constructor() {}
  },
  Dismiss: class {
    static {
      this.type = '[Maintenance] Dismiss';
    }
    constructor() {}
  },
  EnableMaintenanceMode: class {
    static {
      this.type = '[Maintenance] EnableMaintenanceMode';
    }
    constructor(maintenanceModeText = null, whitelisted = false) {
      this.maintenanceModeText = maintenanceModeText;
      this.whitelisted = whitelisted;
    }
  }
};
export let MaintenanceFacade = /*#__PURE__*/(() => {
  class MaintenanceFacade {
    constructor(store) {
      this.store = store;
    }
    dismiss() {
      this.store.dispatch(new MaintenanceActions.Dismiss());
    }
    enableMaintenanceMode(maintenanceModeText, whitelisted = false) {
      this.store.dispatch(new MaintenanceActions.EnableMaintenanceMode(maintenanceModeText, whitelisted));
    }
    static {
      this.ɵfac = function MaintenanceFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MaintenanceFacade)(i0.ɵɵinject(i1.Store));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: MaintenanceFacade,
        factory: MaintenanceFacade.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MaintenanceFacade;
})();
let MaintenanceState = /*#__PURE__*/(() => {
  let MaintenanceState = class MaintenanceState {
    static maintenanceModeEnabled(state) {
      return state.maintenanceMode;
    }
    static whitelisted(state) {
      return state.whitelisted;
    }
    static maintenanceModeText(state) {
      return state.maintenanceModeText;
    }
    static announcement(state) {
      if (state.announcementText) {
        if (state.dismissedDate) {
          // if this message has been dismissed by user, dont show it again.
          const dismissedDate = dayjs(state.dismissedDate);
          if (!dismissedDate.isBefore(state.announcementTime)) return null;
        }
        return state.announcementText;
      }
      return null;
    }
    constructor(config, access, api, persistenceFactory, ngPlatformId) {
      this.config = config;
      this.access = access;
      this.api = api;
      this.persistenceFactory = persistenceFactory;
      this.ngPlatformId = ngPlatformId;
      this.persistedDismissedDate = this.persistenceFactory.create(STORAGE_ID_DISMISSED_DATE, false, 'local');
    }
    ngxsOnInit(ctx) {
      if (!isPlatformServer(this.ngPlatformId)) {
        if (!this.config.get('maintenance.maintenanceMode', false)) {
          // 	// no need to try to fetch maintenance announcement if maintenanceMode=true in config
          // 	if(this.access.isSU) ctx.dispatch(new MaintenanceActions.EnableSUMaintenanceMode());
          // 	else ctx.dispatch(new MaintenanceActions.EnableMaintenanceMode());
          // } else {
          ctx.dispatch(new MaintenanceActions.FetchAnnouncement());
          const dismissedDate = this.persistedDismissedDate.get();
          if (dismissedDate) ctx.patchState({
            dismissedDate: new Date(dismissedDate)
          });
        }
      }
    }
    fetchAnnouncement(ctx, action) {
      this.api.sendRequest(CheckMaintenanceInfoRequest).subscribe(response => {
        // eslint-disable-next-line prefer-const
        let {
          announcementTime,
          announcementText
        } = response;
        if (typeof announcementTime === 'string') announcementTime = new Date(announcementTime);
        ctx.patchState({
          announcementText,
          announcementTime
        });
        setTimeout(() => {
          ctx.dispatch(new MaintenanceActions.FetchAnnouncement());
        }, 15 * 60 * 1000); // 15 minutes interval
      });
    }
    dismiss(ctx, action) {
      const state = ctx.getState();
      ctx.patchState({
        dismissedDate: state.announcementTime
      });
      this.persistedDismissedDate.set(state.announcementTime.toISOString());
    }
    enableMaintenanceMode(ctx, action) {
      ctx.patchState({
        maintenanceMode: true,
        whitelisted: action.whitelisted,
        maintenanceModeText: action.maintenanceModeText
      });
    }
    static {
      this.ɵfac = function MaintenanceState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MaintenanceState)(i0.ɵɵinject(i2.ConfigService), i0.ɵɵinject(i3.AccessService), i0.ɵɵinject(i4.AppRequestService), i0.ɵɵinject(i3.PersistStateFactory), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: MaintenanceState,
        factory: MaintenanceState.ɵfac
      });
    }
  };
  __decorate([Action(MaintenanceActions.FetchAnnouncement), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], MaintenanceState.prototype, "fetchAnnouncement", null);
  __decorate([Action(MaintenanceActions.Dismiss), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], MaintenanceState.prototype, "dismiss", null);
  __decorate([Action(MaintenanceActions.EnableMaintenanceMode), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], MaintenanceState.prototype, "enableMaintenanceMode", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], MaintenanceState, "maintenanceModeEnabled", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], MaintenanceState, "whitelisted", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], MaintenanceState, "maintenanceModeText", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], MaintenanceState, "announcement", null);
  MaintenanceState = __decorate([State({
    name: 'maintenance',
    defaults: {
      announcementText: null,
      announcementTime: null,
      dismissedDate: null,
      maintenanceMode: false,
      maintenanceModeText: null,
      whitelisted: false
    }
  }), __metadata("design:paramtypes", [ConfigService, AccessService, AppRequestService, PersistStateFactory, Object])], MaintenanceState);
  return MaintenanceState;
})();
export { MaintenanceState };